import React from "react";
import { matchPath, useLocation } from "react-router";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  colors,
  Divider,
  Drawer,
  List,
  ListSubheader,
} from "@material-ui/core";
import firebase from "firebase/app";

import * as Sentry from "@sentry/browser";
import _ from "lodash";
import DocumentationIcon from "@material-ui/icons/MenuBookOutlined";
import RestoreOutlinedIcon from "@material-ui/icons/RestoreOutlined";

import { LocalStorageProject } from "./reducers/environments";
import {
  isAccountInHIPAAMode,
  makeRequest,
  masqueradeToken,
  stopMasquerading,
  userIsFPEmployee,
} from "./util";

import NavItem, { NavItemProps } from "./NavItem";
import NavBarSkeleton from "./NavBarSkeleton";

import {
  CodeIcon,
  CustomerInsightsManagerIcon,
  DashboardIcon,
  DatabaseIcon,
  DatabaseRestoreIcon,
  FilmNoirIcon,
  FiltrationIcon,
  GoalIcon,
  IncomingDataIcon,
  IndexIcon,
  MulticastIcon,
  OnlineIcon,
  OpenInBrowserIcon,
  RightViewIcon,
  SettingsIcon,
  SignOutIcon,
  SplitIcon,
  StaffIcon,
  SwapIcon,
  WebsiteIcon,
} from "./components/icons/themes/windows10";
import { SupportAgentIcon as SupportIcon } from "./components/icons/themes/materialdesign";
import { AddChartIcon as ChartIcon } from "./components/icons/themes/materialdesign";

import useAccountMetadata from "./hooks/useAccountMetadata";
import useCurrentProject from "./hooks/use-project";
import Troubleshoot from "./components/icons/svg/material-design/Troubleshoot";
import SvgIcons8Dashboard from "./components/icons/svg/windows-10/Icons8Dashboard";
import MultipleChoiceIcon from "./components/icons/svg/windows-10/Icons8MultipleChoice";
import useFeatureFlags from "./hooks/useFeatureFlag";
import useWTMURLs from "./trackers/useWTMURLs";
import SearchIcon from "@material-ui/icons/SearchOutlined";

export function signout() {
  stopMasquerading();
  makeRequest("invalidate_token")
    .then(() => {
      void firebase.auth().signOut();
    })
    .catch((error) => {
      Sentry.captureException(error);
      void firebase.auth().signOut();
    });
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 195,
    top: 64,
    height: "calc(100% - 64px)",
  },
  desktopDrawerIsMasquerading: {
    width: 195,
    top: 93,
    height: "calc(100% - 93px)",
  },
  navigation: {
    overflow: "auto",
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  profile: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  badge: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
  badgeDot: {
    height: 9,
    minWidth: 9,
  },
  onlineBadge: {
    backgroundColor: colors.green[600],
  },
  awayBadge: {
    backgroundColor: colors.orange[600],
  },
  busyBadge: {
    backgroundColor: colors.red[600],
  },
  offlineBadge: {
    backgroundColor: colors.grey[300],
  },
  avatar: {
    cursor: "pointer",
    width: 40,
    height: 40,
  },
  details: {
    marginLeft: theme.spacing(2),
  },
  moreButton: {
    marginLeft: "auto",
    color: colors.blueGrey[200],
  },
}));

type renderNavItemsPropsItem = NavItemProps & {
  items?: renderNavItemsPropsItem[];
  shouldShowFn?: () => boolean;
};

type renderNavItemsProps = {
  depth?: number;
  items?: renderNavItemsPropsItem[];
  key?: string;
  pathname?: string;
  subheader?: string;
};

function renderNavItems({
  depth = 0,
  items = [],
  pathname,
  subheader,
  key,
  ...rest
}: renderNavItemsProps) {
  items = items.filter((item) => !item.shouldShowFn || item.shouldShowFn());
  return (
    <List key={key}>
      {subheader && <ListSubheader disableSticky>{subheader}</ListSubheader>}
      {/* eslint-disable-next-line react/prop-types */}
      {items.reduce(
        // eslint-disable-next-line no-use-before-define
        (acc, item) => reduceChildRoutes({ acc, item, pathname, ...rest }),
        [] as JSX.Element[]
      )}
    </List>
  );
}

type reduceChildRoutesProps = {
  acc: JSX.Element[];
  pathname?: string;
  item: renderNavItemsPropsItem;
  depth?: number;
};

function reduceChildRoutes({
  acc,
  pathname = "",
  item,
  depth = 0,
}: reduceChildRoutesProps) {
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: item.exact || false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={item.href + item.title}
        label={item.label}
        open={Boolean(open)}
        title={item.title}
        className={item.className}
        activeHrefPrefixes={item.activeHrefPrefixes}
        exact={item.exact}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        openNewTab={item.openNewTab}
        onClick={item.onClick}
        icon={item.icon}
        key={item.href + item.title}
        label={item.label}
        title={item.title}
        activeHrefPrefixes={item.activeHrefPrefixes}
        exact={item.exact}
        skipReactRouter={item.skipReactRouter}
      />
    );
  }

  return acc;
}

export type NavBarProps = {
  className?: string;
};

function NavBar({ className, ...rest }: NavBarProps) {
  const classes = useStyles();
  const location = useLocation();
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const wtmURLs = useWTMURLs();

  const currentProject = useCurrentProject();
  let hasGlobalAllowListEntries = false;
  if (currentProject.loaded) {
    let p = currentProject as LocalStorageProject;
    hasGlobalAllowListEntries =
      !_.isEmpty(p.eventPropertySensitivityLevels) ||
      !_.isEmpty(p.userPropertySensitivityLevels) ||
      !_.isEmpty(p.groupPropertySensitivityLevels);
  }

  const topLevelNavItems: renderNavItemsPropsItem[] = [];

  const platformNavItems = [
    {
      title: "Setup",
      href: "/sources",
      icon: IncomingDataIcon,
    },
  ];

  if (accountMetadata.hasObjectSources) {
    platformNavItems.push({
      title: "Objects",
      href: "/object-sources",
      icon: RightViewIcon,
    });
  }

  if (accountMetadata.config.sourceFunctions) {
    platformNavItems.push({
      title: "Functions",
      href: "/source-functions",
      icon: CodeIcon,
    });
  }

  const destinationNavItems = [
    {
      title: "Apps",
      href: "/destinations/apps",
      icon: MulticastIcon,
    },
    {
      title: "Warehouses",
      href: "/destinations/warehouses",
      icon: DatabaseRestoreIcon,
    },
  ];

  const webTrackerMonitoringNavItems = [
    {
      title: "Web Trackers",
      href: wtmURLs.trackersOverview,
      icon: Troubleshoot,
      shouldShowFn: () => flag("webTrackerMonitoring"),
    },
    {
      title: "PHI Allowlist",
      href: wtmURLs.phiAllowlist,
      icon: GoalIcon,
      shouldShowFn: () =>
        flag("webTrackerMonitoring") && flag("unifiedDestinationsUI"),
    },
  ];

  if (accountMetadata.config.functions) {
    destinationNavItems.push({
      title: "Functions",
      href: "/destinations/functions",
      icon: CodeIcon,
    });
  }

  const functionsNavItems = [
    {
      title: "Setup",
      href: "/functions",
      icon: CodeIcon,
    },
  ];

  const eventNavItems = [
    {
      title: "Visual Tagger",
      href: "/events/editor",
      icon: OpenInBrowserIcon,
    },
    {
      title: "Library",
      href: flag("displayNewEventLibraryPage")
        ? "/events/library"
        : "/events/schema",
      icon: IndexIcon,
    },
    {
      title: "HIPAA Allow List",
      href: "/hipaa/allowlist",
      shouldShowFn: () =>
        accountMetadata &&
        hasGlobalAllowListEntries &&
        isAccountInHIPAAMode(accountMetadata),
      icon: FiltrationIcon,
    },
    {
      title: "Transformations",
      href: "/transformations",
      icon: SwapIcon,
    },
  ];

  const investigateNavItems = [
    {
      title: "Live View",
      href: "/events/liveview",
      icon: OnlineIcon,
    },
    {
      title: "Event Verification",
      href: "/event-verification",
      icon: RestoreOutlinedIcon,
    },
  ];

  if (accountMetadata.config.sqlTransformations) {
    eventNavItems.push({
      title: "SQL Editor",
      href: "/sql-editor",
      icon: CodeIcon,
    });
  }

  const cohortingNavItems = [
    {
      title: "Audiences",
      href: "/cohorts",
      icon: StaffIcon,
      activeHrefPrefixes: ["/cohort", "/cohorts"],
    },
    {
      title: "Destinations",
      href: "/destinations/cohorting",
      icon: CustomerInsightsManagerIcon,
    },
  ];

  const accountNavItems: renderNavItemsPropsItem[] = [
    {
      title: "Account",
      href: "/settings",
      icon: SettingsIcon,
      items: [
        {
          title: "Team",
          href: "/settings/team",
        },
        {
          title: "Projects",
          href: "/settings/projects",
        },
        {
          title: "API Allowlist",
          href: "/settings/api-allowlist",
          shouldShowFn: () => flag("networkEventDefs"),
        },
      ],
    },
    {
      title: "Sign Out",
      icon: SignOutIcon,
      onClick: (e) => {
        e.preventDefault();
        signout();
      },
      href: "/signout",
    },
  ];

  const supportNavItems = [
    {
      title: "Documentation",
      href: "https://docs.freshpaint.io",
      openNewTab: true,
      icon: DocumentationIcon,
      skipReactRouter: true,
    },
    {
      title: "Support",
      href: "https://support.freshpaint.io",
      openNewTab: true,
      icon: SupportIcon,
      skipReactRouter: true,
    },
  ];

  const adminNavItems = [
    {
      title: "Masquerade",
      href: "/masquerade",
      shouldShowFn: userIsFPEmployee,
      icon: FilmNoirIcon,
    },
    {
      title: "MTU Usage",
      href: "/admin/mtureports",
      shouldShowFn: userIsFPEmployee,
      icon: ChartIcon,
    },
    {
      title: "Entitlements",
      href: "/admin/entitlements",
      shouldShowFn: userIsFPEmployee,
      icon: MultipleChoiceIcon,
    },
  ];

  const wtmAdminNavItems = [
    {
      title: "Catalog",
      href: "/admin/trackers/catalog",
      icon: IndexIcon,
      shouldShowFn: userIsFPEmployee,
    },
    {
      title: "Onboarded Domains",
      href: "/admin/trackers/domainAdmin",
      icon: WebsiteIcon,
      shouldShowFn: userIsFPEmployee,
    },
    {
      title: "Set Up Domain",
      href: "/admin/trackers/setUpDomain",
      icon: SettingsIcon,
      shouldShowFn: userIsFPEmployee,
    },
    {
      title: "Latest Reports",
      href: "/admin/trackers/reportAdmin",
      icon: DatabaseIcon,
      shouldShowFn: userIsFPEmployee,
    },
    {
      title: "Search Reports By Tracker",
      href: "/admin/trackers/reportsByTrackerDomain",
      icon: SearchIcon,
      shouldShowFn: userIsFPEmployee,
    },
    {
      title: "Scan Sites",
      href: "/admin/trackers/siteScanner",
      icon: SvgIcons8Dashboard,
      shouldShowFn: userIsFPEmployee,
    },
  ];

  const accountMetadataLoaded = accountMetadata.loaded ?? false;

  const isShowingMasqueradeWarningBanner =
    masqueradeToken() !== null &&
    !accountMetadata.config.disableMasqueradeWarningBanner;

  return (
    <Drawer
      anchor="left"
      classes={{
        paper: isShowingMasqueradeWarningBanner
          ? classes.desktopDrawerIsMasquerading
          : classes.desktopDrawer,
      }}
      open
      variant="persistent"
    >
      <div {...rest} className={clsx(classes.root, className)}>
        <nav className={classes.navigation}>
          <div>
            {accountMetadataLoaded ? (
              <>
                {renderNavItems({
                  items: topLevelNavItems,
                  subheader: undefined,
                  pathname: location.pathname,
                })}
                {renderNavItems({
                  items: platformNavItems,
                  subheader: "SOURCES",
                  pathname: location.pathname,
                })}
                {renderNavItems({
                  items: eventNavItems,
                  subheader: "EVENTS",
                  pathname: location.pathname,
                })}
                {renderNavItems({
                  items: destinationNavItems,
                  subheader: "DESTINATIONS",
                  pathname: location.pathname,
                })}
                {accountMetadata.config.webTrackerMonitoring &&
                  renderNavItems({
                    items: webTrackerMonitoringNavItems,
                    subheader: "MONITORING",
                    pathname: location.pathname,
                  })}
                {accountMetadata.config.functions &&
                  renderNavItems({
                    items: functionsNavItems,
                    subheader: "FUNCTIONS",
                    pathname: location.pathname,
                  })}
                {accountMetadata.config.personas &&
                  renderNavItems({
                    items: cohortingNavItems,
                    subheader: "COHORTS",
                    pathname: location.pathname,
                  })}
                {renderNavItems({
                  items: investigateNavItems,
                  subheader: "INVESTIGATE",
                  pathname: location.pathname,
                })}
              </>
            ) : (
              <NavBarSkeleton />
            )}
          </div>
          <div>
            {renderNavItems({
              items: supportNavItems,
              subheader: "HELP & SUPPORT",
              pathname: location.pathname,
            })}
            {renderNavItems({
              items: accountNavItems,
              subheader: "ACCOUNT",
              pathname: location.pathname,
            })}
            {userIsFPEmployee() &&
              renderNavItems({
                items: adminNavItems,
                subheader: "ADMIN",
                pathname: location.pathname,
              })}
            {userIsFPEmployee() &&
              renderNavItems({
                items: wtmAdminNavItems,
                subheader: "WTM ADMIN",
                pathname: location.pathname,
              })}
          </div>
        </nav>
        {/* @ts-ignore TODO: where does this classname get defined? does it really exist? */}
        <Divider className={classes.divider} />
      </div>
    </Drawer>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
